import { useState, useEffect, useCallback, useContext } from "react";
import {
  getPrograms,
  getProgramDetail,
} from "../../views/Executive/viewServices/apiCalls";
import AuthContext from "../../connections/auth/AuthProvider.js";

/**
 * Hook for managing Program data in Executive dashboard
 * Programs group scenarios by type (actual/forecast/policy) for result tracking
 */
export const useProgramData = () => {
  const { user, authTokens } = useContext(AuthContext);

  // State for programs list
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // State for selected program
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [selectedProgramDetail, setSelectedProgramDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  /**
   * Fetch list of programs (active only by default)
   */
  const fetchPrograms = useCallback(
    async (status = "active") => {
      // Don't fetch if user isn't properly authenticated
      if (!user || !authTokens?.access) {
        console.warn("⚠️ User not authenticated, skipping program fetch");
        return [];
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log("🔍 Fetching programs from API...");
        const response = await getPrograms(status);
        const programsList = response.data || [];
        console.log("✅ Received", programsList.length, "programs");

        setPrograms(programsList);
        setIsInitialized(true);
        return programsList;
      } catch (err) {
        console.error("❌ Failed to fetch programs:", err);
        setError(err);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [user, authTokens],
  );

  /**
   * Fetch detailed information for a specific program
   */
  const fetchProgramDetail = useCallback(
    async (programId) => {
      if (!programId) {
        setSelectedProgramDetail(null);
        return null;
      }

      // Don't fetch if user isn't properly authenticated
      if (!user || !authTokens?.access) {
        console.warn(
          "⚠️ User not authenticated, skipping program detail fetch",
        );
        return null;
      }

      try {
        setIsLoadingDetail(true);

        console.log("🔍 Fetching program detail for ID:", programId);
        const response = await getProgramDetail(programId);
        const detail = response.data || null;
        console.log("✅ Received program detail:", detail?.program_name);

        setSelectedProgramDetail(detail);
        return detail;
      } catch (err) {
        console.error("❌ Failed to fetch program detail:", err);
        setSelectedProgramDetail(null);
        return null;
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [user, authTokens],
  );

  /**
   * Select a program and fetch its details
   */
  const selectProgram = useCallback(
    async (programId) => {
      setSelectedProgramId(programId);
      if (programId) {
        await fetchProgramDetail(programId);
      } else {
        setSelectedProgramDetail(null);
      }
    },
    [fetchProgramDetail],
  );

  /**
   * Refresh programs list
   */
  const refreshPrograms = useCallback(async () => {
    return await fetchPrograms("active");
  }, [fetchPrograms]);

  // Initial data fetch
  useEffect(() => {
    console.log("🚀 useProgramData MOUNTING - Initial data fetch");

    const initializeData = async () => {
      // Wait for authentication to be ready
      if (!user || !authTokens?.access) {
        console.log("⏳ Waiting for user authentication to be ready...");
        return;
      }

      const programsList = await fetchPrograms("active");

      // Auto-select first program if available
      if (programsList.length > 0 && !selectedProgramId) {
        console.log(
          "🎯 Auto-selecting first program:",
          programsList[0].program_name,
        );
        selectProgram(programsList[0].program_id);
      }
    };

    initializeData();
  }, [
    user,
    authTokens?.access,
    fetchPrograms,
    selectProgram,
    selectedProgramId,
  ]);

  console.log("🎯 useProgramData state:", {
    totalPrograms: programs.length,
    selectedProgramId,
    selectedProgramName: selectedProgramDetail?.program_name,
    isLoading,
    isLoadingDetail,
  });

  return {
    // Programs list
    programs,
    isLoading,
    error,
    isInitialized,

    // Selected program
    selectedProgramId,
    selectedProgramDetail,
    isLoadingDetail,

    // Actions
    selectProgram,
    refreshPrograms,
    fetchProgramDetail,
  };
};
